import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Loader2, Trash2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { mailAPI } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';

/**
 * Compose modal supporting:
 *  - initialDraft: pre-populate from draft/reply/forward
 *  - autosave: saves to /mail/drafts as user types (debounced)
 *  - on close with content: user chooses "Save draft" or "Discard"
 *  - on send: related draft is deleted server-side via send payload
 */
export default function ComposeEmailModal({ isOpen, onClose, initialDraft = null, onSent, onDraftSaved }) {
    const [sending, setSending] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [showCc, setShowCc] = useState(false);
    const [showBcc, setShowBcc] = useState(false);
    const [draftId, setDraftId] = useState(null);
    const [formData, setFormData] = useState({
        to: '', cc: '', bcc: '', subject: '', body: ''
    });
    const autosaveTimer = useRef(null);
    const lastSavedPayload = useRef('');

    // Initialize/reset when opened or when initialDraft changes
    useEffect(() => {
        if (isOpen) {
            const src = initialDraft || {};
            setFormData({
                to: src.to_email || src.to || '',
                cc: src.cc || '',
                bcc: src.bcc || '',
                subject: src.subject || '',
                body: src.body || '',
            });
            setDraftId(src.id || null);
            setShowCc(Boolean(src.cc));
            setShowBcc(Boolean(src.bcc));
            lastSavedPayload.current = '';
        } else {
            // cleanup autosave timer when closed
            if (autosaveTimer.current) {
                clearTimeout(autosaveTimer.current);
                autosaveTimer.current = null;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, initialDraft?.id]);

    const hasContent = () => {
        return Boolean(
            (formData.to || '').trim() ||
            (formData.cc || '').trim() ||
            (formData.bcc || '').trim() ||
            (formData.subject || '').trim() ||
            (formData.body || '').trim()
        );
    };

    // Debounced autosave
    useEffect(() => {
        if (!isOpen || sending) return;
        if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
        if (!hasContent()) return;
        const payload = JSON.stringify(formData);
        if (payload === lastSavedPayload.current) return;
        autosaveTimer.current = setTimeout(async () => {
            try {
                setSavingDraft(true);
                const res = await mailAPI.saveDraft({
                    id: draftId || undefined,
                    to: formData.to || null,
                    cc: formData.cc || null,
                    bcc: formData.bcc || null,
                    subject: formData.subject || null,
                    body: formData.body || null,
                });
                if (res?.data?.id) setDraftId(res.data.id);
                lastSavedPayload.current = payload;
            } catch (err) {
                console.error('Autosave draft failed', err);
            } finally {
                setSavingDraft(false);
            }
        }, 1500);
        return () => {
            if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData, isOpen, draftId, sending]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSend = async () => {
        if (!formData.to.trim()) { toast.error('Email penerima harus diisi'); return; }
        if (!formData.subject.trim()) { toast.error('Subjek email harus diisi'); return; }
        if (!formData.body.trim()) { toast.error('Isi email harus diisi'); return; }

        try {
            setSending(true);
            await mailAPI.sendEmail({
                to: formData.to,
                subject: formData.subject,
                body: formData.body,
                cc: formData.cc || undefined,
                bcc: formData.bcc || undefined,
                draft_id: draftId || undefined,
            });
            toast.success('Email berhasil dikirim!');
            setFormData({ to: '', cc: '', bcc: '', subject: '', body: '' });
            setDraftId(null);
            if (onSent) onSent();
            onClose();
        } catch (error) {
            console.error('Failed to send email:', error);
            const errorMsg = error.response?.data?.detail || 'Gagal mengirim email';
            toast.error(typeof errorMsg === 'string' ? errorMsg : 'Gagal mengirim email');
        } finally {
            setSending(false);
        }
    };

    const handleDiscardDraft = async () => {
        try {
            if (draftId) {
                await mailAPI.deleteDraft(draftId);
                toast.success('Draf dihapus');
            }
        } catch (err) {
            console.error('Delete draft failed', err);
        }
        setFormData({ to: '', cc: '', bcc: '', subject: '', body: '' });
        setDraftId(null);
        if (onDraftSaved) onDraftSaved();
        onClose();
    };

    const handleSaveDraftAndClose = async () => {
        if (!hasContent()) {
            onClose();
            return;
        }
        try {
            setSavingDraft(true);
            const res = await mailAPI.saveDraft({
                id: draftId || undefined,
                to: formData.to || null,
                cc: formData.cc || null,
                bcc: formData.bcc || null,
                subject: formData.subject || null,
                body: formData.body || null,
            });
            if (res?.data?.id) setDraftId(res.data.id);
            toast.success('Draf disimpan');
            if (onDraftSaved) onDraftSaved();
        } catch (err) {
            console.error('Save draft failed', err);
            toast.error('Gagal menyimpan draf');
        } finally {
            setSavingDraft(false);
            onClose();
        }
    };

    const handleClose = () => {
        if (sending) return;
        // If there's content — attempt a best-effort save before closing
        if (hasContent() && !draftId) {
            handleSaveDraftAndClose();
        } else {
            onClose();
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="compose-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center justify-between">
                        <span className="flex items-center gap-2">
                            <Send className="w-5 h-5" />
                            {draftId ? 'Edit Draf' : 'Tulis Email'}
                        </span>
                        {savingDraft && (
                            <span className="text-xs text-brand-text-tertiary flex items-center gap-1">
                                <Loader2 className="w-3 h-3 animate-spin" /> Menyimpan draf...
                            </span>
                        )}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-3 py-4">
                    {/* To */}
                    <div className="flex items-center gap-2 border-b border-brand-border pb-2">
                        <Label className="min-w-[80px] text-brand-text-secondary">Kepada</Label>
                        <Input
                            name="to" type="email" placeholder="recipient@example.com"
                            value={formData.to} onChange={handleChange} disabled={sending}
                            className="border-0 shadow-none focus-visible:ring-0"
                            data-testid="compose-to-input"
                        />
                        <div className="flex gap-1">
                            {!showCc && (
                                <Button type="button" variant="ghost" size="sm" onClick={() => setShowCc(true)} className="text-xs" data-testid="compose-show-cc-btn">Cc</Button>
                            )}
                            {!showBcc && (
                                <Button type="button" variant="ghost" size="sm" onClick={() => setShowBcc(true)} className="text-xs" data-testid="compose-show-bcc-btn">Bcc</Button>
                            )}
                        </div>
                    </div>

                    {/* Cc */}
                    {showCc && (
                        <div className="flex items-center gap-2 border-b border-brand-border pb-2">
                            <Label className="min-w-[80px] text-brand-text-secondary">Cc</Label>
                            <Input name="cc" placeholder="cc@example.com (pisahkan dengan koma)"
                                value={formData.cc} onChange={handleChange} disabled={sending}
                                className="border-0 shadow-none focus-visible:ring-0"
                                data-testid="compose-cc-input" />
                            <Button type="button" variant="ghost" size="icon"
                                onClick={() => { setShowCc(false); setFormData(p => ({ ...p, cc: '' })); }}>
                                <X className="w-4 h-4" />
                            </Button>
                        </div>
                    )}

                    {/* Bcc */}
                    {showBcc && (
                        <div className="flex items-center gap-2 border-b border-brand-border pb-2">
                            <Label className="min-w-[80px] text-brand-text-secondary">Bcc</Label>
                            <Input name="bcc" placeholder="bcc@example.com (pisahkan dengan koma)"
                                value={formData.bcc} onChange={handleChange} disabled={sending}
                                className="border-0 shadow-none focus-visible:ring-0"
                                data-testid="compose-bcc-input" />
                            <Button type="button" variant="ghost" size="icon"
                                onClick={() => { setShowBcc(false); setFormData(p => ({ ...p, bcc: '' })); }}>
                                <X className="w-4 h-4" />
                            </Button>
                        </div>
                    )}

                    {/* Subject */}
                    <div className="flex items-center gap-2 border-b border-brand-border pb-2">
                        <Label className="min-w-[80px] text-brand-text-secondary">Subjek</Label>
                        <Input name="subject" placeholder="Subjek email"
                            value={formData.subject} onChange={handleChange} disabled={sending}
                            className="border-0 shadow-none focus-visible:ring-0"
                            data-testid="compose-subject-input" />
                    </div>

                    {/* Body */}
                    <div className="mt-4">
                        <Textarea name="body" placeholder="Tulis pesan Anda..."
                            value={formData.body} onChange={handleChange} disabled={sending}
                            rows={15} className="resize-none"
                            data-testid="compose-body-input" />
                    </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-brand-border gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                        <Button onClick={handleSend} disabled={sending} className="px-8" data-testid="compose-send-btn">
                            {sending ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Mengirim...</>) : (<><Send className="w-4 h-4 mr-2" />Kirim</>)}
                        </Button>
                        {draftId && (
                            <Button variant="outline" size="sm" onClick={handleDiscardDraft} disabled={sending} data-testid="compose-discard-draft-btn">
                                <Trash2 className="w-4 h-4 mr-2" /> Hapus Draf
                            </Button>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={handleSaveDraftAndClose} disabled={sending} data-testid="compose-save-draft-btn">
                            <FileText className="w-4 h-4 mr-2" /> Simpan Draf
                        </Button>
                        <Button variant="ghost" onClick={handleClose} disabled={sending} data-testid="compose-cancel-btn">Tutup</Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
